import React from "react";
import { useParams, Link } from "react-router-dom";
import { Phone, Mail } from "lucide-react";
import SEO, { SITE_URL } from "../components/SEO";
import NotFound from "./NotFound";
import { brand } from "../data/site";
import { legalDocs, getLegalDoc, LEGAL_UPDATED } from "../data/legal";

const Legal = () => {
  const { slug } = useParams();
  const doc = getLegalDoc(slug);
  if (!doc) return <NotFound />;

  const url = `${SITE_URL}/${doc.slug}`;

  return (
    <div>
      <SEO
        title={doc.seoTitle}
        description={doc.seoDescription}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: doc.title,
          url,
          dateModified: "2026-09-19",
          publisher: { "@id": `${SITE_URL}/#business` },
        }}
      />

      <section className="pt-36 pb-12 section-dark text-white">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <nav aria-label="Breadcrumb" className="text-sm text-white/60 mb-5">
            <Link to="/" className="hover:text-brand transition-colors">Home</Link> <span className="mx-2">/</span>
            <span className="text-white/80">{doc.title}</span>
          </nav>
          <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tight">{doc.title}</h1>
          <div className="brand-line my-5" />
          <p className="text-white/60 text-sm">Last updated: {LEGAL_UPDATED}</p>
        </div>
      </section>

      <section className="py-16 bg-background">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <p className="text-lg text-ink/80 leading-relaxed mb-12">{doc.intro}</p>

          {doc.sections.map((s) => (
            <div key={s.h} className="mb-10">
              <h2 className="font-heading text-2xl uppercase tracking-wide mb-4">{s.h}</h2>
              {s.body?.map((t) => (
                <p key={t.slice(0, 40)} className="text-muted-foreground leading-relaxed mb-4">{t}</p>
              ))}
              {s.list && (
                <ul className="space-y-3 mt-2">
                  {s.list.map((li) => (
                    <li key={li.slice(0, 40)} className="flex gap-3 text-muted-foreground leading-relaxed">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand shrink-0 mt-2.5" />
                      <span>{li}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          <div className="border-t border-ink/10 pt-8 mt-12 flex flex-wrap gap-6">
            <a href={`tel:${brand.phoneRaw}`} className="inline-flex items-center gap-2 font-semibold hover:text-brand transition-colors">
              <Phone size={18} className="text-brand" /> {brand.phone}
            </a>
            <a href={`mailto:${brand.emailRaw || brand.email}`} className="inline-flex items-center gap-2 font-semibold hover:text-brand transition-colors">
              <Mail size={18} className="text-brand" /> {brand.email}
            </a>
          </div>

          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-10 text-sm">
            {legalDocs.filter((d) => d.slug !== doc.slug).map((d) => (
              <Link key={d.slug} to={`/${d.slug}`} className="text-muted-foreground hover:text-brand transition-colors">
                {d.title}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Legal;
